import { useState, useMemo } from 'react';
import { FileText, TrendingUp, TrendingDown, X } from 'lucide-react';
import { Card } from '@components/ui';
import clsx from 'clsx';

/**
 * IslemGecmisi — İşlem Geçmişi Sekmesi
 * Filtreler: Tür (Gelir/Gider), Kategori dropdown
 */
export default function IslemGecmisi({ allTransactions = [], formatCurrency, formatDate }) {
    const [filterType, setFilterType] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [showAll, setShowAll] = useState(false);

    const categories = useMemo(() => [...new Set(allTransactions.map(t => t.category))].sort(), [allTransactions]);

    const filteredTx = useMemo(() => allTransactions.filter(t => {
        if (filterType !== 'all' && t.type !== filterType) return false;
        if (filterCategory !== 'all' && t.category !== filterCategory) return false;
        return true;
    }), [allTransactions, filterType, filterCategory]);

    const displayedTx = showAll ? filteredTx : filteredTx.slice(0, 12);
    const hasFilters = filterType !== 'all' || filterCategory !== 'all';

    // Toplam gelir/gider (filtrelenmiş)
    const filteredIncome = filteredTx.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const filteredExpense = filteredTx.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);

    return (
        <Card padding="lg">
            {/* Header */}
            <div className="flex flex-col gap-4 mb-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                        <FileText size={18} className="text-text-muted" />
                        Tüm İşlemler
                        <span className="text-sm font-normal text-text-tertiary ml-1">
                            ({filteredTx.length}{filteredTx.length !== allTransactions.length ? ` / ${allTransactions.length}` : ''})
                        </span>
                    </h3>
                    {hasFilters && (
                        <button onClick={() => { setFilterType('all'); setFilterCategory('all'); }}
                            className="flex items-center gap-1 text-xs text-text-muted hover:text-error transition-colors">
                            <X size={12} /> Temizle
                        </button>
                    )}
                </div>

                {/* Filters + Summary Row */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-1 bg-bg-tertiary rounded-lg p-1">
                        {[{ key: 'all', label: 'Tümü' }, { key: 'income', label: '↓ Gelir' }, { key: 'expense', label: '↑ Gider' }].map(opt => (
                            <button key={opt.key} onClick={() => setFilterType(opt.key)}
                                className={clsx("px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                                    filterType === opt.key ? "bg-bg-secondary text-text-primary shadow-sm" : "text-text-muted hover:text-text-secondary")}>
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                        className="appearance-none px-3 py-1.5 text-xs bg-bg-tertiary border border-border-light rounded-lg text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30">
                        <option value="all">Tüm Kategoriler</option>
                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>

                    {/* Mini summary */}
                    <div className="ml-auto flex items-center gap-4 text-xs">
                        <span className="text-success font-semibold">↓ {formatCurrency(filteredIncome)}</span>
                        <span className="text-error font-semibold">↑ {formatCurrency(filteredExpense)}</span>
                        <span className={clsx("font-bold", (filteredIncome - filteredExpense) >= 0 ? 'text-primary' : 'text-error')}>
                            Net: {formatCurrency(filteredIncome - filteredExpense)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-border-light">
                            <th className="text-left py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">Tarih</th>
                            <th className="text-left py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">Tür</th>
                            <th className="text-left py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">Kategori</th>
                            <th className="text-left py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider hidden md:table-cell">Kişi / Firma</th>
                            <th className="text-left py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider hidden lg:table-cell">Açıklama</th>
                            <th className="text-right py-3 px-2 text-xs font-semibold text-text-muted uppercase tracking-wider">Tutar</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedTx.map(tx => (
                            <tr key={tx.id} className="border-b border-border-light last:border-0 hover:bg-bg-hover transition-colors">
                                <td className="py-3 px-2 text-sm text-text-primary">{formatDate(tx.date)}</td>
                                <td className="py-3 px-2">
                                    <span className={clsx("inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                                        tx.type === 'income' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-rose-500/10 text-rose-600')}>
                                        {tx.type === 'income' ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                                        {tx.type === 'income' ? 'Gelir' : 'Gider'}
                                    </span>
                                </td>
                                <td className="py-3 px-2 text-sm text-text-secondary">{tx.category}</td>
                                <td className="py-3 px-2 text-sm text-text-primary hidden md:table-cell">{tx.person}</td>
                                <td className="py-3 px-2 text-sm text-text-muted hidden lg:table-cell">{tx.description}</td>
                                <td className={clsx("py-3 px-2 text-sm font-bold text-right",
                                    tx.type === 'income' ? 'text-success' : 'text-error')}>
                                    {tx.type === 'income' ? '+' : '−'}{formatCurrency(tx.amount)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredTx.length > 12 && (
                <div className="mt-4 text-center">
                    <button onClick={() => setShowAll(!showAll)}
                        className="text-sm text-primary hover:text-primary-dark font-medium transition-colors">
                        {showAll ? 'Daralt ↑' : `Tümünü Göster (${filteredTx.length} kayıt) →`}
                    </button>
                </div>
            )}
            {filteredTx.length === 0 && (
                <div className="py-8 text-center text-text-muted">
                    <FileText size={32} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Seçili filtreye uygun işlem bulunamadı.</p>
                </div>
            )}
        </Card>
    );
}
